/* @flow */
// typecheck: ignore file
import cx from 'classnames';
import React from 'react';
import invariant from 'invariant';
import { Container } from 'flux/utils';
import Activity from 'entities/Activity';
import ChatStore from 'chat/stores/ChatStore';
import CurrentUser from 'auth/CurrentUser';
import Dialog from 'components/Dialog.jsx';
import enumerate from 'lib/enumerate';
import GroupMessageStarter from 'chat/components/GroupMessageStarter.jsx';
import GroupStore from 'groups/stores/GroupStore';
import IndividualMessageStarter from 'chat/components/IndividualMessageStarter.jsx';
import Message from 'entities/Message';
import MessageStore from 'groups/stores/MessageStore';
import RoleBasedMessagingComposer from 'chat/components/RoleBasedMessagingComposer.jsx';
import Spacing from 'styles/Spacing.css';
import SettingsStore from 'stores/SettingsStore';
import TitleHeader from 'components/TitleHeader.jsx';
import { getDefaultGroups } from 'lib/getDefaultGroups';

import { prefillMessageWithGroupName } from 'utils/prefillMessageWithGroupName';
import { TEACHER } from 'utils/roleHelpers';

import type { Target } from 'entities/Message';

import UniversalComposerTabView from './UniversalComposerTabView.jsx';
import styles from './styles/UniversalComposer.css';

type State = {
  draftAnnouncement: ?Message,
};

export const PANELS = enumerate([
  'CLASS_ANNOUCEMENT',
  'GROUP_CONVERSATION',
  'INDIVIDUAL_MESSAGE',
]);

class UniversalComposer extends React.Component {
  state: State;

  static contextTypes = {
    router: React.PropTypes.object.isRequired,
  };

  static propTypes = {
    canAnnounce: React.PropTypes.bool,
    panel: React.PropTypes.oneOf([
      PANELS.CLASS_ANNOUCEMENT,
      PANELS.GROUP_CONVERSATION,
      PANELS.INDIVIDUAL_MESSAGE,
    ]),
    openActivityOnMount: React.PropTypes.bool,
    activity: React.PropTypes.instanceOf(Activity),
    onMessageSent: React.PropTypes.func,
  };

  static getStores() {
    return [GroupStore, ChatStore];
  }

  static calculateState(): State {
    return {
      draftAnnouncement: GroupStore.getDraftAnnouncement(),
    };
  }

  getGroupCode(): ?string {
    const { activity } = this.props;
    if (this.context.router.params.groupCode) {
      return this.context.router.params.groupCode;
    }

    if (activity) {
      const groupSummaries = activity.getGroupSummaries();
      if (groupSummaries.length > 0) {
        return groupSummaries[0].name;
      }
    }
    return null;
  }

  getInitialAnnouncement(): ?Message {
    const { draftAnnouncement } = this.state;
    const { activity } = this.props;

    // Unfinished message takes precedence
    if (draftAnnouncement) {
      return draftAnnouncement;
    }

    const groupCode = this.getGroupCode();

    if (!groupCode) {
      return null;
    }

    const groups = getDefaultGroups(groupCode);

    let initialText = '';
    // If we are routed to classes (aka. coming from the classes screen) and
    // the class we came from has no existing messages, we will provide a prefilled
    // message
    // NOTE: Currently, groups will always be an array of size 0-1, where the one element
    // is the class that we initiated the UniversalComposer from, so we can just check the
    // message count of groups[0] to see if there are messages in the class.
    // If this changes (ie. the implementation of getDefaultGroups changes),
    // then we need to figure out the number of messages in a different way.
    if (groups.length === 1
      && MessageStore.getMessages(groups[0].id).isEmpty()) {
      initialText = prefillMessageWithGroupName(groups[0].getName());
    }

    if (activity) {
      initialText =
        `Hey! I’m using Remind to organize ${activity.getTitle()}. Please respond here --> ${activity.getPayUrl()}`;
    }
    const message = Message.createMessage(groups, initialText);

    const group = GroupStore.getGroupForCode(groupCode);
    invariant(group, 'missing group in getInitialAnnouncement');
    const hasRecipients = message.getGroupIds().length || message.getTargets().length || message.getThreads().length;
    if (!hasRecipients && group.getPostableRoles().includes(TEACHER)) {
      message.set('targets', [{
        role: TEACHER,
        group_id: group.id,
      }]);
    }

    return message;
  }

  handleAnnouncementSent(targets: Target[]) {
    const firstTarget = targets[0];
    const group = GroupStore.getGroup(firstTarget.group_id);
    invariant(group, 'missing group in handleAnnouncementSent');
    const groupCode = group.getCode();
    this.context.router
      .push(`/classes/${groupCode}/messages/announcements/${firstTarget.role}`);
  }

  handleMessageSent = (targets) => {
    this.props.onRequestHide();
    if (this.props.onMessageSent) {
      this.props.onMessageSent();
    }
    this.handleAnnouncementSent(targets);
  }

  getChatMembersLimit() {
    let limit = SettingsStore.getChatMembersLimit();
    try {
      // WHY do we need this try?
      limit = CurrentUser.getUser().getGroupChatMemberLimit();
    } catch (e) {
      console.warn(
        'Could not fetch settings.chats.members.limit for user'
      );
    }

    return limit - 1; // subtract one for yourself
  }

  renderAllPanels() {
    return (
      <Dialog
        {...this.props}
        className={cx(styles.this, this.props.className)}
        eventScreenName="UniversalComposer"
        size="large">
        <UniversalComposerTabView
          title="New message"
          subtext="Choose the type of message you want to send.">
          {
            this.props.canAnnounce &&
              <UniversalComposerTabView.Panel
                className={Spacing.paddingHuge}
                name="Class announcement"
                glyphName="announcement"
                glyphSize="xxlarge"
                defaultSelected>
                {this.renderAnnouncementComposer()}
              </UniversalComposerTabView.Panel>
          }
          <UniversalComposerTabView.Panel
            className={Spacing.paddingHuge}
            name="Group conversation"
            glyphName="message-group"
            glyphSize="xxlarge">
            {this.renderGroupMessageStarter()}
          </UniversalComposerTabView.Panel>
          <UniversalComposerTabView.Panel
            className={Spacing.paddingHuge}
            name="Individual message"
            glyphName="message-individual"
            glyphSize="xxlarge"
            defaultSelected={!this.props.canAnnounce}>
            {this.renderIndividualMessageStarter()}
          </UniversalComposerTabView.Panel>
        </UniversalComposerTabView>
      </Dialog>
    );
  }

  renderGroupMessageStarter() {
    return (
      <GroupMessageStarter
        groupCode={this.getGroupCode() || undefined}
        chatMembersLimit={this.getChatMembersLimit()}
        onRequestHide={this.props.onRequestHide}
      />
    );
  }

  renderIndividualMessageStarter() {
    return (
      <IndividualMessageStarter
        groupCode={this.getGroupCode() || undefined}
        onRequestHide={this.props.onRequestHide}
      />
    );
  }

  renderAnnouncementComposer() {
    return (
      <RoleBasedMessagingComposer
        onRequestHide={this.props.onRequestHide}
        initialMessage={this.getInitialAnnouncement()}
        onMessageSent={this.handleMessageSent}
        charLimit={CurrentUser.getUser().getAnnouncementCharactersLimit()}
        charLimitWithFiles={
          CurrentUser.getUser().getAnnouncementCharactersLimitWithFiles()
        }
      />
    );
  }

  renderPanel(panel) {
    let content;
    let title;
    let subtext;

    switch (panel) {
      case PANELS.CLASS_ANNOUCEMENT:
        title = 'Send a Class Announcement';
        subtext = 'Keep everyone in the loop with class announcements. Schedule them ahead to save time.';
        content = this.renderAnnouncementComposer();
        break;
      case PANELS.GROUP_CONVERSATION:
        title = 'Create a Group Conversation';
        subtext = `Start the dialogue with up to ${this.getChatMembersLimit()} other people—everyone can participate in the conversation.`;
        content = this.renderGroupMessageStarter();
        break;
      case PANELS.INDIVIDUAL_MESSAGE:
        title = 'Create an Individual Message';
        subtext = 'Reach out to someone with questions or feedback.';
        content = this.renderIndividualMessageStarter();
        break;
      default:
        console.warn('unknow universal composer panel: ', panel);
    }

    return (
      <Dialog {...this.props} size="large" className={cx(styles.this, this.props.className)} eventScreenName="UniversalComposer">
        <div className={styles.uniquePanelContent}>
          <TitleHeader textAlign="left" title={title} subtext={subtext} />
          {content}
        </div>
      </Dialog>
    );
  }

  render() {
    return this.props.panel ? this.renderPanel(this.props.panel) : this.renderAllPanels();
  }
}

export default Container.create(UniversalComposer);



// WEBPACK FOOTER //
// ./src/chat/components/UniversalComposer.jsx